import React, { useState, useEffect } from 'react';
import { Col, Row, formatAmountText } from '../../lib/utils';
import { useAmountInputCaret } from '../../lib/amountInputCaret';
import { colors } from '../../lib/colors';
import { Body2Regular, Subtitle1Bold } from '../../lib/font';
import { IFacilityProduct, CreateFacilityProductBody, FacilityProductCategory } from '@/features/facilityProduct/types';

const CATEGORY_OPTIONS: { code: FacilityProductCategory; value: string }[] = [
    { code: 'gym', value: '헬스(GYM)' },
    { code: 'locker', value: '락커' },
    { code: 'clothing', value: '운동복' },
];

interface Props {
    isVisible: boolean;
    onClose: () => void;
    onSubmit: (body: CreateFacilityProductBody & { isActive?: boolean }) => void;
    onDelete?: () => void;
    editProduct?: IFacilityProduct | null;
    isLoading?: boolean;
}

const FacilityProductFormModal = ({ isVisible, onClose, onSubmit, onDelete, editProduct, isLoading }: Props) => {
    const [name, setName] = useState('');
    const [category, setCategory] = useState<FacilityProductCategory>('gym');
    const [duration, setDuration] = useState('');
    // 콤마 없는 숫자 문자열만 담는다. 제출은 그대로 Number(price) 를 쓴다.
    const [price, setPrice] = useState('');
    const amountCaret = useAmountInputCaret({ allowDecimal: true });
    const [description, setDescription] = useState('');
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        // 부모가 값을 갈아끼우는 자리 — 낡은 커서 복원을 버린다.
        amountCaret.dropPendingCaret();
        if (editProduct) {
            setName(editProduct.name);
            setCategory(editProduct.category);
            setDuration(String(editProduct.duration));
            setPrice(String(editProduct.price));
            setDescription(editProduct.description || '');
            setIsActive(editProduct.isActive !== false);
        } else {
            setName('');
            setCategory('gym');
            setDuration('');
            setPrice('');
            setDescription('');
            setIsActive(true);
        }
    }, [editProduct, isVisible, amountCaret]);

    const isValid = name.trim() && duration && Number(duration) > 0 && price && Number(price) >= 0;

    const handleSubmit = () => {
        if (!isValid) return;
        onSubmit({
            name: name.trim(),
            category,
            duration: Number(duration),
            price: Number(price),
            description: description.trim() || undefined,
            ...(editProduct ? { isActive } : {}),
        });
    };

    if (!isVisible) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={onClose}
        >
            <Col
                className="bg-white rounded-[16px] p-[32px] w-[480px]"
                onClick={(e) => e.stopPropagation()}
            >
                <Subtitle1Bold>{editProduct ? '상품 수정' : '상품 등록'}</Subtitle1Bold>

                <Col className="mt-[24px] gap-[16px]">
                    {/* 상품명 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>상품명</Body2Regular>
                        <input
                            className="border border-[#E1E4E6] rounded-[8px] px-[12px] py-[10px] text-[14px]"
                            placeholder="예: 3개월 헬스"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Col>

                    {/* 카테고리 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>카테고리</Body2Regular>
                        <select
                            className="border border-[#E1E4E6] rounded-[8px] px-[12px] py-[10px] text-[14px]"
                            value={category}
                            onChange={(e) => setCategory(e.target.value as FacilityProductCategory)}
                        >
                            {CATEGORY_OPTIONS.map((opt) => (
                                <option key={opt.code} value={opt.code}>
                                    {opt.value}
                                </option>
                            ))}
                        </select>
                    </Col>

                    {/* 기간 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>이용 기간 (일)</Body2Regular>
                        <input
                            className="border border-[#E1E4E6] rounded-[8px] px-[12px] py-[10px] text-[14px]"
                            type="number"
                            placeholder="예: 90"
                            min="1"
                            max="3650"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                        />
                        {duration && Number(duration) <= 0 && (
                            <span className="text-[11px] text-red-500">1일 이상 입력해주세요</span>
                        )}
                    </Col>

                    {/* 가격 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>가격 (원)</Body2Regular>
                        <input
                            className="border border-[#E1E4E6] rounded-[8px] px-[12px] py-[10px] text-[14px]"
                            type="text"
                            inputMode="numeric"
                            placeholder="예: 300,000"
                            value={formatAmountText(price)}
                            onChange={(e) => {
                                // 원칙 1 — 패턴에 안 맞으면 값을 아예 바꾸지 않는다.
                                const digits = amountCaret.changeAmount(e, price);
                                if (digits == null) return;
                                setPrice(digits);
                            }}
                            // 붙여넣기는 한 번에 값이 확정되므로 정규형만 받는다(CSV 조각 차단).
                            onPaste={amountCaret.pasteAmount}
                            onDrop={amountCaret.dropAmount}
                        />
                        {price && Number(price) < 0 && (
                            <span className="text-[11px] text-red-500">0원 이상 입력해주세요</span>
                        )}
                    </Col>

                    {/* 설명 */}
                    <Col className="gap-[6px]">
                        <Body2Regular color={colors.LAVEL_2}>설명 (선택)</Body2Regular>
                        <textarea
                            className="border border-[#E1E4E6] rounded-[8px] px-[12px] py-[10px] text-[14px] resize-none"
                            rows={3}
                            placeholder="상품 설명을 입력해주세요"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Col>

                    {/* 판매 상태 (수정 시에만 표시) */}
                    {editProduct && (
                        <Col className="gap-[6px]">
                            <Body2Regular color={colors.LAVEL_2}>판매 상태</Body2Regular>
                            <Row
                                className="border border-[#E1E4E6] rounded-[8px] overflow-hidden"
                                style={{ height: 42 }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setIsActive(true)}
                                    style={{
                                        flex: 1,
                                        fontSize: 13,
                                        fontWeight: isActive ? 700 : 400,
                                        backgroundColor: isActive ? '#D1FAE5' : 'white',
                                        color: isActive ? '#065F46' : '#9CA3AF',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    ● 판매중
                                </button>
                                <div style={{ width: 1, backgroundColor: '#E1E4E6' }} />
                                <button
                                    type="button"
                                    onClick={() => setIsActive(false)}
                                    style={{
                                        flex: 1,
                                        fontSize: 13,
                                        fontWeight: !isActive ? 700 : 400,
                                        backgroundColor: !isActive ? '#FEE2E2' : 'white',
                                        color: !isActive ? '#DC2626' : '#9CA3AF',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    ○ 판매중지
                                </button>
                            </Row>
                        </Col>
                    )}
                </Col>

                <Row className="mt-[24px] gap-[12px] justify-between items-center">
                    {onDelete ? (
                        <button
                            onClick={onDelete}
                            style={{
                                height: 40,
                                padding: '0 16px',
                                borderRadius: 8,
                                fontSize: 13,
                                fontWeight: 600,
                                backgroundColor: 'white',
                                color: '#DC2626',
                                border: '1px solid #FECACA',
                                cursor: 'pointer',
                            }}
                        >
                            상품 삭제
                        </button>
                    ) : <div />}
                    <Row className="gap-[8px]">
                        <button
                            onClick={onClose}
                            style={{
                                height: 40,
                                padding: '0 20px',
                                borderRadius: 8,
                                fontSize: 13,
                                fontWeight: 600,
                                backgroundColor: '#FFFFFF',
                                color: '#4B5563',
                                border: '1px solid #D1D5DB',
                                cursor: 'pointer',
                            }}
                        >
                            취소
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!isValid || isLoading}
                            style={{
                                height: 40,
                                padding: '0 20px',
                                borderRadius: 8,
                                fontSize: 13,
                                fontWeight: 700,
                                backgroundColor: !isValid || isLoading ? '#CBD5E1' : '#3B82F6',
                                color: 'white',
                                border: 'none',
                                cursor: !isValid || isLoading ? 'default' : 'pointer',
                                transition: 'background-color 0.15s',
                            }}
                        >
                            {editProduct ? '수정 완료' : '등록'}
                        </button>
                    </Row>
                </Row>
            </Col>
        </div>
    );
};

export default FacilityProductFormModal;
